import fs from 'node:fs';
import { getCodeNavCacheSqlitePath } from './code-nav-cache-paths.js';

function isSqliteCorruptError(e: unknown): boolean {
  const msg = e instanceof Error ? e.message : String(e);
  return msg.includes('malformed') || msg.includes('database disk image is malformed') || msg.includes('corrupt');
}

/**
 * Serialize all code-nav-cache SQLite work (file index + symbol index) within this CLI process.
 * Reentrant: nested calls on the same async stack run inline without re-queuing (avoids deadlocks).
 */
let chain: Promise<unknown> = Promise.resolve();
let workLockDepth = 0;

export function resetCodeNavCacheSqliteWorkLock(): void {
  chain = Promise.resolve();
  workLockDepth = 0;
}

export function withCodeNavCacheSqliteWorkLock<T>(fn: () => T | Promise<T>): Promise<T> {
  if (workLockDepth > 0) {
    return Promise.resolve(fn());
  }

  const run = async (): Promise<T> => {
    workLockDepth += 1;
    try {
      return await Promise.resolve(fn());
    } finally {
      workLockDepth = Math.max(0, workLockDepth - 1);
    }
  };

  const runWithCorruptRecovery = async (): Promise<T> => {
    try {
      return await run();
    } catch (e) {
      if (!isSqliteCorruptError(e)) throw e;
      try {
        fs.unlinkSync(getCodeNavCacheSqlitePath());
      } catch {
        /* ignore */
      }
      chain = Promise.resolve();
      return await run();
    }
  };

  const next = chain.then(runWithCorruptRecovery);
  chain = next.then(
    () => undefined,
    () => undefined,
  );
  return next;
}
